import { useEffect, useMemo, useState } from "react";

import { useFlags } from "launchdarkly-react-client-sdk";

import DecryptDataSection from "./components/DecryptDataSection";
import TransactionBeneficiary from "./components/TransactionBeneficiary";
import TransactionInformation from "./components/TransactionInformation";
import TransactionOriginator from "./components/TransactionOriginator";
import TransactionStatus from "./components/TransactionStatus";

import * as Grid from "~/components/Grid";
import { calculateRequiredFields, useJurisdictions } from "~/endpoints/jurisdictions";
import { postTxValidateFull } from "~/endpoints/transactions";
import { useGetVaspSettings, useTfSimpleVaspInfo } from "~/endpoints/trustFramework";
import { useSessionWrap } from "~/hooks/use-session";
import { TransactionWrap } from "~/hooks/use-transaction";
import useCurrentViewerVaspDID from "~/hooks/useCurrentViewerVaspDID";
import useVaspContext from "~/hooks/useVaspContext";

type TransactionDetailsInfoProps = {
  transaction: TransactionWrap;
  onClickOpenApprove?: (id: string) => void;
  onClickOpenCancel?: (id: string) => void;
  onClickOpenAccept?: (id: string) => void;
  onClickOpenDecline?: (id: string) => void;
  onSubmit?: (input: any) => Promise<any>;
};

export const datetime = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  timeZoneName: "short",
});
export const formatterDate = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
});
export const formatterTime = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "numeric",
});

export default function TransactionDetailsInfo({
  transaction,
  onClickOpenApprove,
  onClickOpenCancel,
  onClickOpenAccept,
  onClickOpenDecline,
}: TransactionDetailsInfoProps) {
  const [{ vasp }] = useSessionWrap();
  const [isEncrypted, setIsEncrypted] = useState(true);
  const { data: vaspInfo } = useTfSimpleVaspInfo(vasp?.did, ["did", "jurisdictions", "division"]);
  const { data: jurisdictions } = useJurisdictions({
    countryCode: vaspInfo?.jurisdictions,
    division: vaspInfo?.division,
  });

  const flags = useFlags();
  const vaspContext = useVaspContext();

  const { refreshButtonForSanctions } = useFlags();

  const requiredFields = useMemo(() => {
    if (jurisdictions && jurisdictions.length > 0) {
      return calculateRequiredFields(
        jurisdictions[0],
        Number(transaction?.amountInLocalCurrency?.amountInLocalCurrency || 0),
      );
    }
    return [];
  }, [jurisdictions, transaction]);

  const [, , direction] = transaction?.direction(vasp) || [];
  const isOutgoing = direction === "outgoing";
  const isIncoming = direction === "incoming";

  const [txValidateFull, setTxValidateFull] = useState<any>();

  const vaspDID = useCurrentViewerVaspDID();
  const { data: vaspSettingsResponse } = useGetVaspSettings(vaspDID);

  const displayUnverifiedVaspWarning = useMemo(
    () =>
      flags.omitPiiIvmsVaspsWithoutSubscription
        ? vaspContext.getActiveVasp()?.verificationStatus !== "VERIFIED"
        : undefined,
    [flags.omitPiiIvmsVaspsWithoutSubscription, vaspContext],
  );

  useEffect(() => {
    const doFetch = async () => {
      const data = {
        skipBeneficiaryDataValidation: false,
        transactionAsset: transaction.transactionAsset,
        transactionAmount: transaction.transactionAmount,
        beneficiaryDid: transaction.beneficiaryDid,
        originatorVASPdid: transaction.originatorVASPdid || undefined,
        beneficiaryVASPdid: transaction.beneficiaryVASPdid || undefined,
        transactionBlockchainInfo: transaction.transactionBlockchainInfo,
        originator: transaction.originator,
        beneficiary: transaction.beneficiary,
        ...(transaction.originatorDid && { originatorDid: transaction.originatorDid }), //MISSING ORIGINATOR trx might not have originatorDid
        ...(transaction.custom_asset_price && { customAssetPrice: transaction.custom_asset_price }),
      };
      if (isOutgoing) {
        const txValidationResults = await postTxValidateFull(data);
        setTxValidateFull(txValidationResults);
      }
    };
    doFetch().catch((err) => {
      throw err;
    });
  }, [transaction, isOutgoing]);

  useEffect(() => {
    if (flags.autoDecryptionSetting && vaspSettingsResponse?.autoDecryption && !displayUnverifiedVaspWarning) {
      // Only auto-decrypt if VASP is verified
      setIsEncrypted(false);
    } else if (displayUnverifiedVaspWarning) {
      setIsEncrypted(true); // Force encryption for unverified VASPs
    }
  }, [flags.autoDecryptionSetting, vaspSettingsResponse?.autoDecryption, displayUnverifiedVaspWarning]);

  const { isRejected, isNotReady } = transaction || {};

  const hideDecryptionDueToTxType = isIncoming && (isRejected || isNotReady); //if blockchain address was rejected, we dont show pii to beneficiary

  const hideDecryptionDueToSetting =
    flags.autoDecryptionSetting && vaspSettingsResponse?.autoDecryption && !displayUnverifiedVaspWarning;

  const hideDecryption = hideDecryptionDueToTxType || hideDecryptionDueToSetting;

  return (
    <Grid.Columns multiline>
      <Grid.Column mobile={12}>
        {!hideDecryption && (
          <DecryptDataSection
            isEncryptionEnabled={isEncrypted}
            onClickButton={() => {
              // Only allow manual decryption if VASP is verified
              if (!displayUnverifiedVaspWarning) {
                setIsEncrypted(!isEncrypted);
              }
            }}
            displayUnverifiedVaspWarning={displayUnverifiedVaspWarning}
          />
        )}
      </Grid.Column>

      <Grid.Column mobile={12}>
        <TransactionStatus
          transaction={transaction}
          requiredFields={requiredFields}
          warnings={txValidateFull?.warnings || []}
          onClickOpenApprove={onClickOpenApprove}
          onClickOpenCancel={onClickOpenCancel}
          onClickOpenAccept={onClickOpenAccept}
          onClickOpenDecline={onClickOpenDecline}
        />
      </Grid.Column>

      <Grid.Column mobile={12}>
        <TransactionInformation
          requiredFields={requiredFields}
          transaction={transaction}
          direction={direction}
          warnings={txValidateFull?.warnings || []}
        />
      </Grid.Column>
      <Grid.Column mobile={12}>
        <TransactionOriginator
          requiredFields={requiredFields}
          transaction={transaction}
          direction={direction}
          warnings={txValidateFull?.warnings || []}
          isEncryptionEnabled={isEncrypted}
          refreshButtonForSanctions={refreshButtonForSanctions}
        />
      </Grid.Column>

      <Grid.Column mobile={12}>
        <TransactionBeneficiary
          requiredFields={requiredFields}
          transaction={transaction}
          direction={direction}
          warnings={txValidateFull?.warnings || []}
          isEncryptionEnabled={isEncrypted}
          refreshButtonForSanctions={refreshButtonForSanctions}
        />
      </Grid.Column>
    </Grid.Columns>
  );
}
